import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  useThreadMessages,
  useSendMessage,
  useMarkThreadRead,
  type Message,
  type MessageThread as MessageThreadType,
} from './hooks/useMessages';

interface MessageThreadProps {
  thread: MessageThreadType;
}

/**
 * Message thread view with chronological messages, internal notes,
 * auto-scroll, and message input with internal note toggle.
 * Validates: Requirements 19.1
 */
export function MessageThread({ thread }: MessageThreadProps) {
  const { data: messages, isLoading } = useThreadMessages(thread.id);
  const sendMessage = useSendMessage();
  const markRead = useMarkThreadRead();

  const [content, setContent] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Track threads already marked read this mount so we don't re-fire on every
  // list refetch (which produces new `thread` objects). Prevents the
  // mark-read → invalidate → refetch → re-render → mark-read storm.
  const markedReadRef = useRef<Set<string>>(new Set());

  // Mark thread as read once when a thread with unread messages is opened.
  useEffect(() => {
    if (thread.unreadCount > 0 && !markedReadRef.current.has(thread.id)) {
      markedReadRef.current.add(thread.id);
      markRead.mutate(thread.id);
    }
    // Intentionally keyed only on thread.id — `markRead` and `unreadCount`
    // are excluded to avoid re-running when the mutation object or a refetched
    // thread object changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!content.trim()) return;

    await sendMessage.mutateAsync({
      threadId: thread.id,
      content: content.trim(),
      isInternalNote,
    });

    setContent('');
    setIsInternalNote(false);
    textareaRef.current?.focus();
  }, [content, isInternalNote, thread.id, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Send on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Thread header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {thread.clientName}
          </h2>
          {thread.subject && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{thread.subject}</p>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        )}

        {messages && messages.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No messages yet. Start the conversation below.
            </p>
          </div>
        )}

        {messages && messages.length > 0 && (
          <div className="space-y-4">
            {messages.map((message: Message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
        {/* Internal note indicator */}
        {isInternalNote && (
          <div className="mb-2 flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-1.5 dark:bg-yellow-900/20">
            <svg
              className="h-4 w-4 text-yellow-600 dark:text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
              Internal note — not visible to client
            </span>
          </div>
        )}

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isInternalNote ? 'Write an internal note...' : 'Type a message...'}
              rows={2}
              className={`w-full resize-none rounded-lg border px-4 py-2.5 text-sm transition-colors
                focus:outline-none focus:ring-2
                ${
                  isInternalNote
                    ? 'border-yellow-300 bg-yellow-50 focus:ring-yellow-300 dark:border-yellow-700 dark:bg-yellow-900/20 dark:focus:ring-yellow-700'
                    : 'border-gray-300 bg-white focus:ring-brand-300 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-brand-700'
                }
                text-gray-900 placeholder-gray-400 dark:text-gray-100 dark:placeholder-gray-500`}
              aria-label={isInternalNote ? 'Internal note input' : 'Message input'}
            />
          </div>

          <div className="flex flex-col gap-2">
            {/* Internal note toggle */}
            <Button
              type="button"
              variant={isInternalNote ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setIsInternalNote((prev) => !prev)}
              aria-label="Toggle internal note"
              aria-pressed={isInternalNote}
              className={isInternalNote ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </Button>

            {/* Send button */}
            <Button
              onClick={handleSend}
              disabled={!content.trim() || sendMessage.isPending}
              size="sm"
              aria-label="Send message"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </Button>
          </div>
        </div>

        <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}

// ─── Message Bubble Component ────────────────────────────────────────────────

interface MessageBubbleProps {
  message: Message;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isStaff = message.senderType === 'staff';
  const isNote = message.isInternalNote;

  return (
    <div className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[70%] rounded-lg px-4 py-2.5 ${
          isNote
            ? 'border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/30'
            : isStaff
              ? 'bg-brand-600 text-white dark:bg-brand-700'
              : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
        }`}
      >
        {/* Internal note badge */}
        {isNote && (
          <Badge variant="warning" size="sm" className="mb-1">
            Internal Note
          </Badge>
        )}

        {/* Sender name */}
        <p
          className={`text-xs font-medium ${
            isNote
              ? 'text-yellow-700 dark:text-yellow-300'
              : isStaff
                ? 'text-brand-100'
                : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {message.senderName}
        </p>

        {/* Message content */}
        <p
          className={`mt-1 whitespace-pre-wrap text-sm ${
            isNote
              ? 'text-yellow-900 dark:text-yellow-100'
              : isStaff
                ? 'text-white'
                : 'text-gray-900 dark:text-gray-100'
          }`}
        >
          {message.content}
        </p>

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((attachment) => (
              <a
                key={attachment.id}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1.5 text-xs underline ${
                  isStaff && !isNote
                    ? 'text-brand-100 hover:text-white'
                    : 'text-brand-600 hover:text-brand-700 dark:text-brand-400'
                }`}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
                {attachment.fileName}
              </a>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <p
          className={`mt-1 text-xs ${
            isNote
              ? 'text-yellow-600 dark:text-yellow-400'
              : isStaff
                ? 'text-brand-200'
                : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          {new Date(message.createdAt).toLocaleString()}
        </p>
      </div>
    </div>
  );
}
